<script lang="ts">
  // Where the docked mini player is drawn. Registers a slot with `players` while this element is visible and
  // publishes its box, which the player pins itself to. The player is never re-parented into the slot —
  // that would reload the IMA ad iframes — so a host whose slot moves with the page passes `follow`, and the
  // fixed player is nudged along vertically.

  import { players, type SlotHandle } from './players.svelte'

  const { follow = false }: { follow?: boolean } = $props()

  let element: HTMLElement | undefined = $state()

  // Hosts switch the slot off with CSS; the ResizeObserver only triggers, checkVisibility decides.
  let usable = $state(false)
  let box = $state.raw<{ left: number; width: number } | undefined>()

  $effect(() => {
    if (!element) {
      return
    }
    const target = element
    const observer = new ResizeObserver(() => {
      usable = target.checkVisibility()
      const { left, width } = target.getBoundingClientRect()
      box = { left, width }
    })
    observer.observe(target)
    return () => observer.disconnect()
  })

  let slot = $state.raw<SlotHandle | undefined>()

  $effect(() => {
    if (!usable) {
      return
    }
    const handle = players.registerSlot()
    slot = handle
    return () => {
      slot = undefined
      handle.release()
    }
  })

  $effect(() => {
    if (slot && box) {
      // The slot itself may be flat (the Unpoly ghost has no height until a player docks); the player is 16:9.
      slot.setGeometry({ left: box.left, width: box.width, height: (box.width * 9) / 16 })
    }
  })

  $effect(() => {
    const player = players.docked
    if (!follow || !player || !element) {
      return
    }
    return followSlot(element, player.container)
  })

  function followSlot(slotElement: HTMLElement, container: HTMLElement): () => void {
    let offset = 0
    let frame = 0
    let following = false

    function sync(): void {
      frame = 0
      const drift = slotElement.getBoundingClientRect().top - container.getBoundingClientRect().top
      if (Math.abs(drift) < 0.5) {
        return
      }
      // += not =: drift is measured against the box as already transformed.
      offset += drift
      container.style.transform = `translateY(${offset}px)`
    }

    function schedule(): void {
      if (following && !frame) {
        frame = requestAnimationFrame(sync)
      }
    }

    // Deferred a frame: `-docked` lands in this same update, and only a fixed container may move.
    const started = requestAnimationFrame(() => {
      if (getComputedStyle(container).position !== 'fixed') {
        return
      }
      following = true
      sync()
    })

    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule, { passive: true })

    return () => {
      cancelAnimationFrame(started)
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      container.style.transform = ''
    }
  }
</script>

<!-- `data-pip-slot` is unstyled; it is what the system spec measures the docked player against. -->
<div class="slot" data-pip-slot bind:this={element}></div>

<style>
  .slot {
    width: 100%;
    height: 100%;
  }
</style>
